import { parseMoneyToCents, tt, type MoneyInput } from '@agos/shared'

/**
 * The loyalty programme's tiers, and the two places they are configured from.
 *
 * **FB-097.** `/loyalty-program` has two editors for one thing. `LoyaltyLevelsEditor` writes
 * `loyalty_levels` — key, name, emoji, colour, `min_orders`, `multiplier`. The *Body za vernosť*
 * block below it writes eight columns on `loyalty_settings`: `silver_threshold`, `gold_threshold`,
 * `gold_master_threshold`, `elite_master_threshold` and the four matching multipliers. Same five
 * tiers, same numbers, two save buttons.
 *
 * Only one of them decides anything:
 *
 * ```java
 * // CustomerLoyalty.tierFor
 * if (customTiers.isEmpty()) {
 *   String key = orderCount >= settings.eliteMasterThreshold() ? "elite_master" : … ;
 *   return new Tier(null, key, settings.multiplierFor(key));
 * }
 * CustomTier picked = customTiers.get(0);
 * for (CustomTier candidate : customTiers) {
 *   if (orderCount >= candidate.minOrders()) picked = candidate;
 * }
 * ```
 *
 * `customTiers` is `SELECT key, min_orders, multiplier FROM loyalty_levels WHERE active = true`, and
 * it has five rows. `LoyaltyLevelService.backfillCustomerLevels` reads the same table. So the eight
 * `loyalty_settings` columns are a fallback for a franchise that predates the tier editor — and this
 * franchise does not. Raise the Gold threshold from 100 to 60 in the *Body za vernosť* block, press
 * save, get *"Nastavenia bodov uložené"*, and nobody moves.
 *
 * Nobody has noticed because the two agree today, digit for digit: 50/100/150/200 with
 * 1,00/1,20/1,40/1,70 in `loyalty_settings`, and 0/50/100/150/200 with 1,00/1,00/1,20/1,40/1,70 in
 * `loyalty_levels`. The moment one is edited they diverge in silence.
 *
 * **FB-098.** Four of the five tiers are out of reach. 1 108 loyalty members: 1 106 on Bronze, one on
 * Silver with 53 orders, one on Gold Master with 184. Nobody on Gold, nobody on Elite Master. The
 * busiest Bronze customer has **44** orders against a Silver threshold of 50. So the multipliers
 * above 1,0 have applied to two customers in the programme's life, and the page that sets them says
 * nothing about it.
 */

// ─── the tiers as the server has them ───────────────────────────────────────

export interface Tier {
  id?: number
  key?: string
  name?: string
  emoji?: string | null
  color?: string | null
  minOrders?: number
  multiplier?: MoneyInput
  sortOrder?: number
  active?: boolean
  isStarter?: boolean
}

/** The five keys the `loyalty_level` enum column accepts. A custom tier maps onto the nearest. */
export const FIXED_KEYS = ['bronze', 'silver', 'gold', 'gold_master', 'elite_master'] as const

export type FixedKey = (typeof FIXED_KEYS)[number]

/** Weakest first — the order the server resolves in, and the order they are shown. */
export function byThreshold(tiers: Tier[]): Tier[] {
  return [...tiers].sort((left, right) => {
    const byOrders = (left.minOrders ?? 0) - (right.minOrders ?? 0)
    if (byOrders !== 0) return byOrders
    const bySort = (left.sortOrder ?? 0) - (right.sortOrder ?? 0)
    if (bySort !== 0) return bySort
    return (left.id ?? 0) - (right.id ?? 0)
  })
}

export function activeTiers(tiers: Tier[]): Tier[] {
  return byThreshold(tiers.filter((tier) => tier.active !== false))
}

/**
 * The tier a customer with this many orders earns — `CustomerLoyalty.tierFor`, exactly.
 *
 * The last tier whose threshold is met, walking upwards, with the weakest as the floor. Ported so
 * the page can say who a threshold change would actually move, which is the only way to tell that
 * FB-097's inert fields are inert.
 */
export function tierFor(orderCount: number, tiers: Tier[]): Tier | null {
  const ladder = activeTiers(tiers)
  if (ladder.length === 0) return null
  let picked = ladder[0] as Tier
  for (const candidate of ladder) {
    if (orderCount >= (candidate.minOrders ?? 0)) picked = candidate
  }
  return picked
}

// ─── does the settings block still matter? ──────────────────────────────────

export interface PointsSettings {
  pointsPerEuro?: MoneyInput
  minRedeemPoints?: number
  pointValue?: MoneyInput
  silverThreshold?: number
  goldThreshold?: number
  goldMasterThreshold?: number
  eliteMasterThreshold?: number
  silverMultiplier?: MoneyInput
  goldMultiplier?: MoneyInput
  goldMasterMultiplier?: MoneyInput
  eliteMasterMultiplier?: MoneyInput
}

export interface ProgramSettings extends PointsSettings {
  isActive?: boolean
  programName?: string
  description?: string
  welcomeMessage?: string
  labelBronze?: string
  labelSilver?: string
  labelGold?: string
  labelGoldMaster?: string
  labelEliteMaster?: string
  enabledSilver?: boolean
  enabledGold?: boolean
  enabledGoldMaster?: boolean
  enabledEliteMaster?: boolean
}

/**
 * Whether the eight `loyalty_settings` tier columns decide anything.
 *
 * They do only when no active row exists in `loyalty_levels` — `if (customTiers.isEmpty())`. So this
 * is the sentence the page needs before it shows those fields at all.
 */
export function settingsTiersAreLive(tiers: Tier[]): boolean {
  return activeTiers(tiers).length === 0
}

export interface Divergence {
  key: FixedKey
  /** What `loyalty_settings` says. */
  settingsThreshold: number
  settingsMultiplier: number
  /** What `loyalty_levels` says — the one that counts. */
  tierThreshold: number | null
  tierMultiplier: number | null
}

const SETTINGS_THRESHOLD: Record<Exclude<FixedKey, 'bronze'>, keyof PointsSettings> = {
  silver: 'silverThreshold',
  gold: 'goldThreshold',
  gold_master: 'goldMasterThreshold',
  elite_master: 'eliteMasterThreshold',
}

const SETTINGS_MULTIPLIER: Record<Exclude<FixedKey, 'bronze'>, keyof PointsSettings> = {
  silver: 'silverMultiplier',
  gold: 'goldMultiplier',
  gold_master: 'goldMasterMultiplier',
  elite_master: 'eliteMasterMultiplier',
}

/**
 * Where the two configurations disagree, tier by tier.
 *
 * They agree today. This exists so that the first divergence is visible on the screen rather than in
 * a customer's points balance — and so the page can prove which side won.
 */
export function divergences(settings: ProgramSettings, tiers: Tier[]): Divergence[] {
  const ladder = activeTiers(tiers)
  const found: Divergence[] = []

  for (const key of ['silver', 'gold', 'gold_master', 'elite_master'] as const) {
    const tier = ladder.find((candidate) => candidate.key === key)
    const settingsThreshold = Number(settings[SETTINGS_THRESHOLD[key]] ?? 0)
    const settingsMultiplier = numberOf(settings[SETTINGS_MULTIPLIER[key]])
    const tierThreshold = tier === undefined ? null : (tier.minOrders ?? 0)
    const tierMultiplier = tier === undefined ? null : numberOf(tier.multiplier)

    if (tierThreshold !== settingsThreshold || tierMultiplier !== settingsMultiplier) {
      found.push({ key, settingsThreshold, settingsMultiplier, tierThreshold, tierMultiplier })
    }
  }

  return found
}

function numberOf(value: MoneyInput): number {
  if (value === null || value === undefined || value === '') return 0
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

export { numberOf as rateOf }

// ─── who is actually on each tier ───────────────────────────────────────────

export interface TierPopulation {
  key: string
  name: string
  minOrders: number
  members: number
  /** The busiest customer who has not reached the next tier up. */
  highestBelow: number | null
  reachable: boolean
}

export interface Distribution {
  members: number
  tiers: TierPopulation[]
  /** Tiers nobody has ever reached. */
  emptyTiers: string[]
  /** How many orders the closest customer still needs for the first empty tier. */
  shortfall: number | null
}

/**
 * How the members are spread across the ladder, and which rungs nobody stands on.
 *
 * **FB-098.** Counted from the customers the page already has, because the server offers no such
 * figure — the rule FB-060 set: derived, and labelled as derived. A settings page that lets somebody
 * set a 1,7× multiplier on a tier two customers have ever reached should say so.
 */
export function distribution(
  orderCounts: number[],
  tiers: Tier[],
): Distribution {
  const ladder = activeTiers(tiers)
  const counts = new Map<string, number>()
  const highest = new Map<string, number>()

  for (const orderCount of orderCounts) {
    const tier = tierFor(orderCount, ladder)
    if (tier?.key === undefined) continue
    counts.set(tier.key, (counts.get(tier.key) ?? 0) + 1)
    highest.set(tier.key, Math.max(highest.get(tier.key) ?? 0, orderCount))
  }

  const populations: TierPopulation[] = ladder.map((tier) => ({
    key: tier.key ?? '',
    name: tier.name ?? tier.key ?? '',
    minOrders: tier.minOrders ?? 0,
    members: counts.get(tier.key ?? '') ?? 0,
    highestBelow: highest.get(tier.key ?? '') ?? null,
    reachable: (counts.get(tier.key ?? '') ?? 0) > 0,
  }))

  const emptyTiers = populations.filter((tier) => tier.members === 0).map((tier) => tier.key)
  const firstEmpty = populations.find((tier) => tier.members === 0)
  const busiest = orderCounts.length === 0 ? 0 : Math.max(...orderCounts)
  const shortfall =
    firstEmpty === undefined ? null : Math.max(0, firstEmpty.minOrders - busiest)

  return { members: orderCounts.length, tiers: populations, emptyTiers, shortfall }
}

/** How many customers a threshold change would move, up or down. */
export function movedBy(orderCounts: number[], before: Tier[], after: Tier[]): number {
  let moved = 0
  for (const orderCount of orderCounts) {
    if (tierFor(orderCount, before)?.key !== tierFor(orderCount, after)?.key) moved += 1
  }
  return moved
}

// ─── the tier editor's form ─────────────────────────────────────────────────

export interface TierForm {
  key: string
  name: string
  emoji: string
  color: string
  minOrders: string
  multiplier: string
  active: boolean
  isStarter: boolean
}

export const EMOJI_PALETTE = [
  '🏆', '🥉', '🥈', '🥇', '👑', '💎', '⭐', '🌟', '🔥', '⚡', '🚀', '🎖️',
  '🏅', '💫', '✨', '🎯', '🦄', '🍕', '🍔', '☕', '🍷', '🌹', '💝', '🎁',
] as const

export function tierFormOf(tier: Tier): TierForm {
  return {
    key: tier.key ?? '',
    name: tier.name ?? '',
    emoji: tier.emoji ?? '🏆',
    color: tier.color ?? '#f59e0b',
    minOrders: String(tier.minOrders ?? 0),
    multiplier: String(numberOf(tier.multiplier) || 1),
    active: tier.active !== false,
    isStarter: tier.isStarter === true,
  }
}

export function emptyTierForm(): TierForm {
  return {
    key: '',
    name: '',
    emoji: '🏆',
    color: '#f59e0b',
    minOrders: '0',
    multiplier: '1',
    active: true,
    isStarter: false,
  }
}

export type TierProblem =
  | { kind: 'no-key' }
  | { kind: 'bad-key'; value: string }
  | { kind: 'duplicate-key'; value: string }
  | { kind: 'no-name' }
  | { kind: 'threshold-not-whole' }
  | { kind: 'threshold-negative' }
  | { kind: 'duplicate-threshold'; other: string }
  | { kind: 'multiplier-below-one' }
  | { kind: 'multiplier-absurd'; value: number }
  | { kind: 'starter-not-zero' }

/** A key has to survive being written into SQL and compared against the enum's five names. */
const KEY_PATTERN = /^[a-z][a-z0-9_]*$/

/**
 * What is wrong with a tier before it is saved.
 *
 * Legacy validates none of this. Two facts make it matter: the key is what
 * `customers.custom_level_key` stores and what `CustomerLoyalty.noDemotion` compares, so a renamed
 * key orphans everybody who held it; and two tiers on the same threshold make the resolution order
 * decide silently which one somebody lands on.
 *
 * A multiplier below 1 is refused, because it means a customer earns *fewer* points for climbing.
 */
export function tierProblems(form: TierForm, others: Tier[]): TierProblem[] {
  const problems: TierProblem[] = []

  const key = form.key.trim()
  if (key === '') problems.push({ kind: 'no-key' })
  else if (!KEY_PATTERN.test(key)) problems.push({ kind: 'bad-key', value: key })
  else if (others.some((tier) => tier.key === key)) {
    problems.push({ kind: 'duplicate-key', value: key })
  }

  if (form.name.trim() === '') problems.push({ kind: 'no-name' })

  const threshold = Number(form.minOrders.trim())
  if (!Number.isFinite(threshold) || !Number.isInteger(threshold)) {
    problems.push({ kind: 'threshold-not-whole' })
  } else if (threshold < 0) {
    problems.push({ kind: 'threshold-negative' })
  } else {
    const clash = others.find(
      (tier) => tier.active !== false && (tier.minOrders ?? 0) === threshold,
    )
    if (clash !== undefined) {
      problems.push({ kind: 'duplicate-threshold', other: clash.name ?? clash.key ?? '' })
    }
  }

  if (form.isStarter && threshold !== 0) problems.push({ kind: 'starter-not-zero' })

  const multiplier = numberOf(form.multiplier)
  if (multiplier < 1) problems.push({ kind: 'multiplier-below-one' })
  else if (multiplier > 10) problems.push({ kind: 'multiplier-absurd', value: multiplier })

  return problems
}

export function canSaveTier(problems: TierProblem[]): boolean {
  return !problems.some((problem) => problem.kind !== 'multiplier-absurd')
}

export function tierPayload(form: TierForm): Record<string, unknown> {
  return {
    key: form.key.trim(),
    name: form.name.trim(),
    emoji: form.emoji,
    color: form.color,
    minOrders: Number(form.minOrders.trim()),
    multiplier: numberOf(form.multiplier),
    active: form.active,
    isStarter: form.isStarter,
  }
}

/** The tiers as they would be after this edit, for counting who moves. */
export function withEditedTier(tiers: Tier[], form: TierForm, editingId: number | null): Tier[] {
  const edited: Tier = {
    id: editingId ?? -1,
    key: form.key.trim(),
    name: form.name.trim(),
    minOrders: Number(form.minOrders.trim()) || 0,
    multiplier: numberOf(form.multiplier),
    active: form.active,
    isStarter: form.isStarter,
  }
  if (editingId === null) return [...tiers, edited]
  return tiers.map((tier) => (tier.id === editingId ? { ...tier, ...edited } : tier))
}

// ─── the programme's own settings ───────────────────────────────────────────

export interface ProgramForm {
  isActive: boolean
  programName: string
  description: string
  welcomeMessage: string
  pointsPerEuro: string
  minRedeemPoints: string
  pointValue: string
}

export function programFormOf(settings: ProgramSettings): ProgramForm {
  return {
    isActive: settings.isActive === true,
    programName: settings.programName ?? '',
    description: settings.description ?? '',
    welcomeMessage: settings.welcomeMessage ?? '',
    pointsPerEuro: String(numberOf(settings.pointsPerEuro) || 1),
    minRedeemPoints: String(settings.minRedeemPoints ?? 100),
    pointValue: String(numberOf(settings.pointValue) || 0.01),
  }
}

export type ProgramProblem =
  | { kind: 'no-name' }
  | { kind: 'points-per-euro-zero' }
  | { kind: 'point-value-zero' }
  | { kind: 'redeem-not-whole' }
  | { kind: 'redeem-negative' }

export function programProblems(form: ProgramForm): ProgramProblem[] {
  const problems: ProgramProblem[] = []
  if (form.programName.trim() === '') problems.push({ kind: 'no-name' })
  if (numberOf(form.pointsPerEuro) <= 0) problems.push({ kind: 'points-per-euro-zero' })
  if (numberOf(form.pointValue) <= 0) problems.push({ kind: 'point-value-zero' })

  const redeem = Number(form.minRedeemPoints.trim())
  if (!Number.isFinite(redeem) || !Number.isInteger(redeem)) problems.push({ kind: 'redeem-not-whole' })
  else if (redeem < 0) problems.push({ kind: 'redeem-negative' })

  return problems
}

export function canSaveProgram(problems: ProgramProblem[]): boolean {
  return problems.length === 0
}

export function programPayload(form: ProgramForm): Record<string, unknown> {
  return {
    isActive: form.isActive,
    programName: form.programName.trim(),
    description: form.description.trim(),
    welcomeMessage: form.welcomeMessage.trim(),
    pointsPerEuro: numberOf(form.pointsPerEuro),
    minRedeemPoints: Number(form.minRedeemPoints.trim()) || 0,
    pointValue: numberOf(form.pointValue),
  }
}

/**
 * What the minimum redemption is worth, and what it takes to earn.
 *
 * 100 points at 0,01 € is 1,00 € off, earned by spending 100 € at one point per euro. Legacy shows
 * the three numbers as separate fields and never multiplies them, so the only way to know what the
 * programme actually offers is to do it by hand.
 */
export interface RedemptionWorth {
  minRedeemPoints: number
  valueCents: number
  eurosToEarn: number
  /** The discount as a share of what was spent to earn it, in basis points. */
  effectiveRateBasisPoints: number
}

export function redemptionWorth(form: ProgramForm): RedemptionWorth {
  const points = Number(form.minRedeemPoints.trim()) || 0
  const pointValue = numberOf(form.pointValue)
  const perEuro = numberOf(form.pointsPerEuro)
  const valueCents = Math.round(points * pointValue * 100)
  const eurosToEarn = perEuro > 0 ? points / perEuro : 0
  const spentCents = Math.round(eurosToEarn * 100)
  return {
    minRedeemPoints: points,
    valueCents,
    eurosToEarn,
    effectiveRateBasisPoints: spentCents > 0 ? Math.round((valueCents / spentCents) * 10_000) : 0,
  }
}

/** `170` → `"1,7×"`. A multiplier, not money — it keeps its decimals. */
export function formatMultiplier(value: MoneyInput): string {
  const parsed = numberOf(value)
  return `${String(Math.round(parsed * 100) / 100).replace('.', ',')}×`
}

export function formatRate(basisPoints: number): string {
  return `${String(Math.round(basisPoints) / 100).replace('.', ',')} %`
}

// `tierLabel` was here and is deleted — §9.35, 2026-08-06. It joined the emoji and the name into one
// string; the screen draws them as two elements, which is why it was never called — a `<span>` for
// the emoji and a truncating `<p>` for the name lays out better than a concatenation, and falls back
// to 🏆 where this fell back to an empty string. Superseded rather than unused, and a helper that
// formats a row worse than the row does is a trap for the next screen that needs one.

export function starterOf(tiers: Tier[]): Tier | null {
  const ladder = activeTiers(tiers)
  return ladder.find((tier) => tier.isStarter === true) ?? ladder[0] ?? null
}

/** A tier nobody can be demoted out of, so deleting it orphans whoever holds it. */
export function holdersOf(key: string, orderCounts: number[], tiers: Tier[]): number {
  let held = 0
  for (const orderCount of orderCounts) {
    if (tierFor(orderCount, tiers)?.key === key) held += 1
  }
  return held
}

export function deleteWarning(tier: Tier, holders: number): string {
  if (tier.isStarter === true) return tt('Základnú úroveň sa zmazať nedá.')
  if (holders > 0) {
    return tt('Túto úroveň drží niekoľko zákazníkov - po zmazaní ich treba prepočítať.')
  }
  return tt('Túto úroveň nikto nedrží.')
}

export { parseMoneyToCents }
